<template>
  <div v-if="$userInfo.isSystemAdministrator">
    <app-detail-header :show-toggle="true"
      :show-add="true"
      :show-delete="false"
      :show-save="false"
      :show-print="false"
      :show-cancel="false"
      @appDetailHeaderButtonClicked="onHeaderButtonClicked" />
    <div class="columns is-gapless">
      <div class="column is-narrow">
        <company-list-side-menu v-model="filter"
          @filter="filterList"
          @reset="resetList" />
      </div>
      <div class="column">
        <div class="m-3">
          <article class="box">
            <bulma-table class="table is-bordered is-striped is-narrow is-fullwidth"
              :columns="columns"
              :page-index="filter.pageIndex"
              :page-size="filter.pageSize"
              :total-rows="totalRows"
              :is-loading="isTableLoading"
              :sort-column="filter.sortColumn"
              :sort-order="filter.sortOrder"
              @pageChanged="onPageChange"
              @sort="sort">
              <tr v-for="(entity, index) in entitySummaries"
                :key="entity.companyId"
                :class="{ 'is-selected' : selectedRow === index }"
                @click="highlightSelected(index, $event)">
                <td>
                  <router-link :to="{ path: `${entity.companyId}/detail` }">
                    {{ entity.name }}
                  </router-link>
                </td>
                <td>{{ entity.abn }}</td>
                <td>{{ entity.address }}</td>
                <td>{{ entity.suburb }}</td>
                <td>{{ entity.state }}</td>
                <td>{{ entity.country }}</td>
                <td>{{ formatExpiryDate(entity.expiryDate) }}</td>
                <td>{{ entity.loginId }}</td>
                <td class="has-text-centered">
                  <span class="icon"
                    :class="[ entity.isActive ? 'has-text-success' : 'has-text-danger' ]">
                    <span class="mso mso-24px">
                      {{ entity.isActive ? 'check' : 'close' }}
                    </span>
                  </span>
                </td>
              </tr>
              <template slot="empty">
                <section class="section">
                  <div class="content has-text-grey has-text-centered">
                    <span icon="icon is-large">
                      <span class="mso mso-fill mso-40px">sentiment_dissatisfied</span>
                    </span>
                    <p>Nothing</p>
                  </div>
                </section>
              </template>
            </bulma-table>
          </article>
        </div>
      </div>
    </div>
    <company-new-modal :active="isNewCompanyModalActive"
      @close="isNewCompanyModalActive = false"
      @create="handleCompanyCreate" />
  </div>
</template>

<script>
import AppDetailHeader from '@/components/AppDetailHeader'
import CompanyListSideMenu from './CompanyListSideMenu'
import CompanyService from './CompanyService'
import BulmaTable from '@/components/BulmaTable'
import { Columns } from './columns'
import CompanyNewModal from './CompanyNewModal.vue'
import { AppHeaderButtonTypes } from '@/enums'
import SuperadminCompanyRoutes from '../../views/superadmin/company/route-types'

export default {
  name: 'CompanyListView',
  components: {
    AppDetailHeader,
    BulmaTable,
    CompanyListSideMenu,
    CompanyNewModal
  },
  mixins: [],
  props: {},
  data: () => {
    return {
      isNewCompanyModalActive: false,
      entitySummaries: {},
      totalRows: 0,
      filter: {
        companyName: '',
        abn: '',
        address: '',
        suburb: '',
        state: '',
        countryCode: 'AU',
        modules: null,
        isActive: 1,
        isHq: false,
        sortColumn: Columns[0].name,
        sortOrder: Columns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 18
      },
      selectedRow: null,
      isTableLoading: false
    }
  },
  computed: {
    columns() {
      return Columns
    },
    filterKey() {
      if (this.$userInfo) {
        return `${this.$userInfo.sessionId}|${this.$route.meta.fkey}`
      } else {
        return ''
      }
    }
  },
  watch: {},
  created() {
    this.retrieveFilter()
    this.getData()
  },
  mounted() {},
  destroyed() {},
  beforeDestroy() {},
  methods: {
    formatExpiryDate(expiryDate) {
      if (!expiryDate) return ''

      const date = new Date(expiryDate)
      date.setHours(0, 0, 0, 0)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    onHeaderButtonClicked(action) {
      switch (action) {
        case AppHeaderButtonTypes.AddNew:
          this.addNew()
          break
        case AppHeaderButtonTypes.Print:
          break
        case AppHeaderButtonTypes.Delete:
          break
        case AppHeaderButtonTypes.Save:
          break
        case AppHeaderButtonTypes.Cancel:
          break
      }
    },
    filterList() {
      this.filter.pageIndex = 1
      this.persistFilter()
      this.getData()
    },
    sort(name, order) {
      this.filter.sortColumn = name
      this.filter.sortOrder = order
      this.persistFilter()
      this.getData()
    },
    resetList() {
      this.filter.companyName = ''
      this.filter.abn = ''
      this.filter.address = ''
      this.filter.suburb = ''
      this.filter.state = ''
      this.filter.countryCode = 'AU'
      this.filter.modules = null
      this.filter.isActive = 1
      this.filter.isHq = false
      this.filter.sortColumn = Columns[0].name
      this.filter.sortOrder = Columns[0].defaultOrder
      this.filter.pageIndex = 1
      this.filter.pageSize = 18
      this.persistFilter()
      this.getData()
    },
    persistFilter() {
      sessionStorage.setItem(this.filterKey, JSON.stringify(this.filter))
    },
    retrieveFilter() {
      const filter = JSON.parse(sessionStorage.getItem(this.filterKey))
      if (filter) {
        this.filter = filter
      }
    },
    onPageChange(pageIndex) {
      this.filter.pageIndex = pageIndex
      this.persistFilter()
      this.getData()
    },
    async getData() {
      this.isTableLoading = true

      if (Array.isArray(this.filter.modules)) {
        this.filter.modules = this.filter.modules.filter((m) => m !== null && m !== undefined)
        if (this.filter.modules.length) {
          this.filter.modules = this.filter.modules.map((m) => m.flexiQuoteModuleId || m)
        }
      }
      this.entitySummaries = await CompanyService.getCompanies(this.filter)
      this.totalRows = this.entitySummaries.length > 0 ? this.entitySummaries[0].totalRows : 0
      this.isTableLoading = false
    },
    highlightSelected(index, event) {
      this.selectedRow = index
    },
    addNew() {
      this.isNewCompanyModalActive = true
    },
    handleCompanyCreate(companyType) {
      this.isNewCompanyModalActive = false
      this.$router.push({
        name: SuperadminCompanyRoutes.CompanyDetail.name,
        params: { companyId: 'new', companyType: companyType }
      })
    }
  }
}
</script>
